import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userAPI, notificationAPI } from '../api/api';
import FaceIdAyari from '../components/Common/FaceIdAyari';
import BildirimAyari from '../components/Common/BildirimAyari';
import './Profile.css';

const Profile = () => {
  const { user, loadUserInfo } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    username: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.Name ?? user.name ?? '',
        surname: user.Surname ?? user.surname ?? '',
        username: user.Username ?? user.username ?? '',
        email: user.Email ?? user.email ?? ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    setSuccess(null);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setPasswordError(null);
    setPasswordSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    setLoading(true);
    try {
      const updateData = {
        name: formData.name,
        surname: formData.surname,
        username: formData.username,
        email: formData.email
      };

      await userAPI.updateProfile(updateData);
      
      // Reload user info to get updated data
      await loadUserInfo();
      
      setSuccess('Profil bilgileriniz başarıyla güncellendi!');
      setIsEditing(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Güncelleme başarısız!';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validate password
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('Lütfen yeni şifre ve şifre tekrar alanlarını doldurun!');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Yeni şifreler eşleşmiyor!');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Şifre en az 6 karakter olmalıdır!');
      return;
    }

    setPasswordLoading(true);
    try {
      const updateData = {
        name: formData.name,
        surname: formData.surname,
        username: formData.username,
        email: formData.email,
        password: passwordData.newPassword
      };

      await userAPI.updateProfile(updateData);
      
      setPasswordSuccess('Şifreniz başarıyla güncellendi!');
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Şifre güncelleme başarısız!';
      setPasswordError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.Name ?? user.name ?? '',
        surname: user.Surname ?? user.surname ?? '',
        username: user.Username ?? user.username ?? '',
        email: user.Email ?? user.email ?? ''
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <p>Kullanıcı bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <h1>Profil Bilgilerim</h1>
          {!isEditing && (
            <button 
              className="btn btn-primary" 
              onClick={() => setIsEditing(true)}
            >
              Düzenle
            </button>
          )}
        </div>

        <BildirimAyari kaydet={notificationAPI.registerDevice} />
        <FaceIdAyari tur="personel" />

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Ad</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              disabled={!isEditing}
              required
            />
          </div>

          <div className="form-group">
            <label>Soyad</label>
            <input
              type="text"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              className="form-control"
              disabled={!isEditing}
              required
            />
          </div>

          <div className="form-group">
            <label>Kullanıcı Adı</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="form-control"
              disabled={!isEditing}
              required
            />
          </div>

          <div className="form-group">
            <label>E-posta</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-control"
              disabled={!isEditing}
              required
            />
          </div>


          {isEditing && (
            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading}
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleCancel}
                disabled={loading}
              >
                İptal
              </button>
            </div>
          )}
        </form>

        {!isEditing && (
          <div className="profile-info">
            <div className="info-item">
              <span className="info-label">Rol:</span>
              <span className="info-value">{user.Role ?? user.role ?? 'N/A'}</span>
            </div>
          </div>
        )}
      </div>

      {/* Şifre Değiştirme Bölümü */}
      <div className="profile-card">
        <div className="profile-header">
          <h1>Şifre Değiştir</h1>
        </div>

        {passwordSuccess && (
          <div className="alert alert-success">
            {passwordSuccess}
          </div>
        )}

        {passwordError && (
          <div className="alert alert-error">
            {passwordError}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <label>Yeni Şifre</label>
            <input
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              className="form-control"
              placeholder="Yeni şifrenizi girin"
              required
            />
          </div>

          <div className="form-group">
            <label>Yeni Şifre Tekrar</label>
            <input
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              className="form-control"
              placeholder="Yeni şifrenizi tekrar girin"
              required
            />
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={passwordLoading}
            >
              {passwordLoading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;

